<template>
	<v-dialog
		:model-value="open"
		:aria-labelledby="dialogTitleId"
		max-width="25rem"
		:width="display.smAndDown.value ? '90%' : '25rem'"
		:z-index="zIndex"
		:persistent="saving"
		@update:model-value="!$event && emit('cancel')">
		<v-card data-test-id="dialog" class="pa-2" elevation="0">
			<v-card-title class="pa-4"><h5 :id="dialogTitleId" data-test-id="dialog-title">Tags</h5></v-card-title>
			<v-card-text data-test-id="dialog-message" class="pa-4">
				<div class="media-tag-selection" :class="{ 'media-tag-selection--mobile': display.smAndDown.value }">
					<div class="media-tag-selection__search">
						<v-text-field
							v-model="search"
							variant="outlined"
							hide-details="auto"
							color="primary"
							v-bind="searchProps"
							:label="searchLabel"
							:append-inner-icon="searchIcon"
							v-on="searchActionEnabled ? searchActionListeners : {}" />
					</div>
					<div ref="scrollContainer" class="media-tag-selection__list">
						<!-- Keep the bounded outer container as the virtual scroller's scroll parent. -->
						<v-list v-if="items.length" density="compact" class="overflow-visible">
							<v-virtual-scroll v-slot="{ item, ...virtualSlot }" :items="items" renderless>
								<v-list-item :ref="'itemRef' in virtualSlot ? virtualSlot.itemRef : undefined" :key="item.id" @click="emit('toggle', item)">
									<template #prepend>
										<v-list-item-action start
											><div @click.stop>
												<v-checkbox
													:model-value="item.selected"
													:aria-label="item.name"
													hide-details
													@update:model-value="emit('toggle', item)" /></div
										></v-list-item-action>
									</template>
									<v-list-item-title class="text-truncate">
										<slot name="chip" :item="item"
											><v-chip variant="tonal" :color="item.baseColor" :title="item.name">{{ item.name }}</v-chip></slot
										>
									</v-list-item-title>
								</v-list-item>
							</v-virtual-scroll>
						</v-list>
						<div v-else-if="loading" class="d-flex align-center justify-center ga-3 pa-5">
							<v-progress-circular indeterminate size="20" color="primary" />{{ loadingText }}
						</div>
						<slot v-if="!loading" name="create" />
						<div v-if="!items.length && !loading && !$slots.create" class="pa-5 text-center">
							{{ emptyText }}
						</div>
					</div>
				</div>
				<p v-if="error" role="alert">{{ error }}</p>
			</v-card-text>
			<v-card-actions class="pa-4 pb-2 d-flex justify-space-between ga-2">
				<v-btn
					id="cancel-action-btn"
					data-test-id="dialog-cancel-button"
					color="grey"
					variant="outlined"
					:disabled="saving"
					@click="emit('cancel', $event)"
					>Cancel</v-btn
				>
				<v-btn
					id="confirm-action-btn"
					data-test-id="dialog-agree-button"
					color="primary"
					variant="flat"
					:loading="saving"
					@click="emit('agree', $event)"
					>Apply</v-btn
				>
			</v-card-actions>
		</v-card>
	</v-dialog>
</template>

<script setup lang="ts">
	import { ref, watch, nextTick, useId } from "vue";
	import { useDisplay } from "vuetify";
	export interface MediaTagOption {
		id: string;
		name: string;
		baseColor?: string;
		selected?: boolean;
	}
	const props = withDefaults(
		defineProps<{
			open: boolean;
			items: MediaTagOption[];
			loading?: boolean;
			saving?: boolean;
			error?: string;
			searchLabel?: string;
			searchIcon?: string;
			searchActionEnabled?: boolean;
			searchProps?: Record<string, unknown>;
			emptyText?: string;
			loadingText?: string;
			zIndex?: number;
		}>(),
		{
			searchLabel: "Search Tags",
			searchIcon: "icon-search-magnifying-glass",
			searchActionEnabled: false,
			emptyText: "No tags match your search.",
			loadingText: "Fetching your tags...",
			zIndex: 9000
		}
	);
	const search = defineModel<string>("search", { default: "" });
	const emit = defineEmits<{
		cancel: [event?: MouseEvent];
		agree: [event?: MouseEvent];
		toggle: [item: MediaTagOption];
		searchAction: [];
	}>();
	const searchActionListeners = {
		keyup: (event: KeyboardEvent) => {
			if (event.key === "Enter") emit("searchAction");
		},
		"click:appendInner": () => emit("searchAction")
	};
	const display = useDisplay();
	const dialogTitleId = `media-tags-${useId()}`;
	const scrollContainer = ref<HTMLElement | null>(null);
	watch(
		() => props.open,
		async (open) => {
			if (!open) return;
			await nextTick();
			if (scrollContainer.value) scrollContainer.value.scrollTop = 0;
		}
	);
</script>

<style scoped>
	/* Admin's dialog presentation, explicit so the host app's Vuetify settings cannot change it. The card radius is
	   !important, like the rounded-xl utility it replaces, because Vuetify's `.v-dialog > .v-overlay__content > .v-card` outranks it. */
	.v-card {
		border-radius: 1.5rem !important;
		font-family: Inter;
	}
	.v-card .v-card-title,
	h5 {
		font-family: "Inter Tight", sans-serif;
		font-size: 1.5rem;
		font-weight: 600;
		line-height: 132%;
		margin: 0;
	}
	:deep(.v-field) {
		border-radius: 0.5rem;
	}
	:deep(.v-label) {
		font-size: 0.875rem;
	}
	.v-btn {
		border-radius: 1.5rem;
		font-family: Inter;
		font-size: 0.875rem;
		font-weight: 600;
		letter-spacing: normal;
		text-transform: none;
	}
	/* The label sits in .v-btn__content, which async's base.css `*` reset sets to normal weight. */
	.v-btn :deep(.v-btn__content) {
		font-weight: inherit;
	}
	.media-tag-selection {
		display: flex;
		flex-direction: column;
		height: 20vh;
	}
	.media-tag-selection--mobile {
		height: 40vh;
	}
	.media-tag-selection__search {
		flex: none;
	}
	.media-tag-selection__list {
		flex: 1;
		overflow-y: auto;
		overflow-x: hidden;
	}
</style>
